'use client'

import { useEffect, useRef, useState, useCallback } from 'react'
import { useSearchParams } from 'next/navigation'
import type { ListingSummary } from '@/lib/feedbridge/types'
import { ListingCard } from './ListingCard'
import { Reveal } from '@/components/Reveal'

interface InfiniteListingGridProps {
  initialListings: ListingSummary[]
  initialFeaturesMap: Record<string, Record<string, unknown>>
  initialUnderOfferMap: Record<string, boolean>
  initialThumbnailMap: Record<string, string>
  initialThumbnailsMap?: Record<string, string[]>
  initialInspectionTimesMap?: Record<string, string>
  initialCoordinatesMap?: Record<string, { lat: number; lng: number }>
  initialTotal: number
  perPage: number
  vertical: string
  /** Only meaningful when vertical==='commercial' — see ListingPage/config.ts. */
  commercialListingType?: 'sale' | 'lease'
  /**
   * @deprecated No longer rendered. Figma's listing grids are 12 × Property Card with no
   * promo tile in any state (4081:48885 / 4081:85950). Kept so existing callers still type-check.
   */
  showAlertsPromo?: boolean
}

export function InfiniteListingGrid({
  initialListings,
  initialFeaturesMap,
  initialUnderOfferMap,
  initialThumbnailMap,
  initialThumbnailsMap,
  initialInspectionTimesMap,
  initialCoordinatesMap,
  initialTotal,
  perPage,
  vertical,
  commercialListingType,
}: InfiniteListingGridProps) {
  const [listings, setListings] = useState(initialListings)
  const [featuresMap, setFeaturesMap] = useState(initialFeaturesMap)
  const [underOfferMap, setUnderOfferMap] = useState(initialUnderOfferMap)
  const [thumbnailMap, setThumbnailMap] = useState(initialThumbnailMap)
  const [thumbnailsMap, setThumbnailsMap] = useState(initialThumbnailsMap || {})
  const [inspectionTimesMap, setInspectionTimesMap] = useState(initialInspectionTimesMap || {})
  const [total, setTotal] = useState(initialTotal)
  const [loading, setLoading] = useState(false)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const searchParams = useSearchParams()

  // Reset when search params change (filters, sort, etc.)
  useEffect(() => {
    setListings(initialListings)
    setFeaturesMap(initialFeaturesMap)
    setUnderOfferMap(initialUnderOfferMap)
    setThumbnailMap(initialThumbnailMap)
    setInspectionTimesMap(initialInspectionTimesMap || {})
    setTotal(initialTotal)
  }, [
    initialListings,
    initialFeaturesMap,
    initialUnderOfferMap,
    initialThumbnailMap,
    initialThumbnailsMap,
    initialInspectionTimesMap,
    initialTotal,
  ])

  const hasMore = listings.length < total

  const loadMore = useCallback(async () => {
    if (loading || !hasMore) return
    setLoading(true)

    const params = new URLSearchParams()
    params.set('vertical', vertical)
    params.set('limit', String(perPage))
    params.set('offset', String(listings.length))
    if (commercialListingType) params.set('commercialListingType', commercialListingType)

    // Forward current filter params
    const sort = searchParams.get('sort')
    const search = searchParams.get('search')
    const surroundingSuburbs = searchParams.get('surroundingSuburbs')
    const radius = searchParams.get('radius')
    const type = searchParams.get('type')
    const minPrice = searchParams.get('minPrice')
    const maxPrice = searchParams.get('maxPrice')
    const bed = searchParams.get('bed')
    const bath = searchParams.get('bath')
    const car = searchParams.get('car')
    const offMarket = searchParams.get('offMarket')

    if (sort) params.set('sort', sort)
    if (search) params.set('search', search)
    if (surroundingSuburbs) params.set('surroundingSuburbs', surroundingSuburbs)
    if (radius) params.set('radius', radius)
    if (type) params.set('type', type)
    if (minPrice) params.set('minPrice', minPrice)
    if (maxPrice) params.set('maxPrice', maxPrice)
    if (bed) params.set('bed', bed)
    if (bath) params.set('bath', bath)
    if (car) params.set('car', car)
    if (offMarket) params.set('offMarket', offMarket)

    try {
      const res = await fetch(`/api/listings?${params.toString()}`)
      const json = await res.json()

      setListings((prev) => [...prev, ...json.data])
      setFeaturesMap((prev) => ({ ...prev, ...json.featuresMap }))
      setUnderOfferMap((prev) => ({ ...prev, ...(json.underOfferMap || {}) }))
      setThumbnailMap((prev) => ({ ...prev, ...(json.thumbnailMap || {}) }))
      setThumbnailsMap((prev) => ({ ...prev, ...(json.thumbnailsMap || {}) }))
      setInspectionTimesMap((prev) => ({ ...prev, ...(json.inspectionTimesMap || {}) }))
      setTotal(json.total)
    } catch (err) {
      console.error('Failed to load more listings:', err)
    } finally {
      setLoading(false)
    }
  }, [loading, hasMore, listings.length, perPage, vertical, commercialListingType, searchParams])

  // Intersection Observer for infinite scroll
  useEffect(() => {
    const sentinel = sentinelRef.current
    if (!sentinel) return

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMore()
        }
      },
      { rootMargin: '400px' },
    )

    observer.observe(sentinel)
    return () => observer.disconnect()
  }, [loadMore])

  if (!listings.length) {
    return (
      <div className="py-20 flex flex-col items-center justify-center gap-4 text-center">
        <p className="text-brand text-[24px] font-display font-normal leading-8 tracking-[-0.1px]">
          No properties found
        </p>
        <p className="text-[#5a595d] text-body-md font-normal max-w-sm">
          Try adjusting your search or filters to find what you&rsquo;re looking for.
        </p>
      </div>
    )
  }

  // Figma's grids are all 12 × Property Card — no promo tile in any state.
  // Stagger by position within the row (max 3 columns), not the flat list
  // index, so cards loaded later via infinite scroll don't inherit an
  // ever-growing delay after they've already scrolled into view.
  const cards = listings.map((listing, index) => (
    <Reveal key={listing.id} variant="slideup" delay={(index % 3) * 100}>
      <ListingCard
        listing={listing}
        features={featuresMap[listing.external_listing_id]}
        underOffer={underOfferMap[listing.external_listing_id]}
        thumbnailUrl={thumbnailMap[listing.external_listing_id]}
        thumbnailUrls={thumbnailsMap[listing.external_listing_id]}
        inspectionTime={inspectionTimesMap[listing.external_listing_id]}
      />
    </Reveal>
  ))

  return (
    <>
      {/* Figma card grid — must match ListingGrid exactly:
            mobile  390: 1 col, row gap 48 (gap/gap-12)
            tablet  768: 2 cols × 336, column gap 32 (gap-8), ROW gap 64 (gap-16)  ← ASYMMETRIC
            desktop 1600: 3 cols × 448, gap 64 both axes (gap/gap-16)
          Tablet is deliberately asymmetric. Figma models each row as its own frame:
          on the Home tablet frame the cards sit at x=0 and x=368 (336 wide → 32 column gap),
          while the row frames sit at y=160, 616 and 1072 on 392-tall rows → 616−552 = 64 and
          1072−1008 = 64. An earlier pass set both axes to 32 and lost 32px per row gap. */}
      <div className="grid grid-cols-1 gap-y-12 md:grid-cols-2 md:gap-x-8 md:gap-y-16 lg:grid-cols-3 lg:gap-16">
        {cards}
      </div>
      {hasMore && (
        // Zero-height sentinel — Figma has no gap below the last card beyond the grid
        // band's own bottom padding. The spinner supplies its own space while loading.
        <div ref={sentinelRef} className="flex justify-center">
          {loading && (
            <div className="flex items-center gap-2 py-12 text-body-sm text-[#000000]/50">
              <div className="w-4 h-4 border-2 border-[#000000]/20 border-t-[#000000]/60 rounded-full animate-spin" />
              Loading more properties...
            </div>
          )}
        </div>
      )}
    </>
  )
}
